import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { TwoFactorSettings } from "@/components/two-factor-settings";
import { AuthMethodBadge } from "@/components/auth-method-badge";

export const Route = createFileRoute("/_authenticated/admin/settings")({
  head: () => ({
    meta: [
      { title: "Settings — Admin" },
      { name: "description", content: "Platform commission, payout threshold and holding period." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminSettings,
});

type Settings = {
  platform_commission_rate: number;
  minimum_payout_amount: number;
  payout_holding_days: number;
  platform_name: string;
  support_email: string;
  platform_tagline: string;
  maintenance_mode: boolean;
  updated_at: string;
  network_holding_days: Record<string, number> | null;
};

const NETWORKS = ["Amazon", "Levanta", "Impact", "ShareASale"] as const;

function AdminSettings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["platform-settings"],
    queryFn: async (): Promise<Settings | null> => {
      const { data, error } = await supabase
        .from("platform_settings")
        .select("platform_commission_rate,minimum_payout_amount,payout_holding_days,platform_name,support_email,platform_tagline,maintenance_mode,updated_at,network_holding_days")
        .eq("id", true)
        .maybeSingle();
      if (error) throw error;
      return data as Settings | null;
    },
  });

  const [ratePct, setRatePct] = useState("");
  const [minPayout, setMinPayout] = useState("");
  const [holdingDays, setHoldingDays] = useState("");
  const [platformName, setPlatformName] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [tagline, setTagline] = useState("");
  const [networkHolds, setNetworkHolds] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!data) return;
    setRatePct((Number(data.platform_commission_rate) * 100).toFixed(2));
    setMinPayout(Number(data.minimum_payout_amount).toFixed(2));
    setHoldingDays(String(data.payout_holding_days));
    setPlatformName(data.platform_name ?? "Pluggz");
    setSupportEmail(data.support_email ?? "support@pluggzofficial.com");
    setTagline(data.platform_tagline ?? "Curated by Creators");
    const nh = data.network_holding_days ?? {};
    const initial: Record<string, string> = {};
    for (const n of NETWORKS) {
      if (nh[n] != null) {
        initial[n] = String(nh[n]);
      } else if (n === "Amazon") {
        // Amazon Associates has no API for returns; default to 60 days to
        // match Amazon's own commission-clearing window. Prevents paying
        // out an Amazon commission before a return could occur.
        initial[n] = "60";
      } else {
        initial[n] = "";
      }
    }
    setNetworkHolds(initial);
  }, [data]);

  const save = useMutation({
    mutationFn: async () => {
      const rate = Number(ratePct) / 100;
      const minAmt = Number(minPayout);
      const days = parseInt(holdingDays, 10);
      if (!Number.isFinite(rate) || rate < 0 || rate > 1) throw new Error("Commission rate must be 0–100%");
      if (!Number.isFinite(minAmt) || minAmt < 0) throw new Error("Minimum payout must be ≥ 0");
      if (!Number.isInteger(days) || days < 0) throw new Error("Holding days must be a non-negative integer");
      const networkPayload: Record<string, number> = {};
      for (const n of NETWORKS) {
        const raw = (networkHolds[n] ?? "").trim();
        if (raw === "") continue;
        const v = parseInt(raw, 10);
        if (!Number.isInteger(v) || v < 0) throw new Error(`${n} holding days must be a non-negative integer`);
        networkPayload[n] = v;
      }
      const { error } = await supabase
        .from("platform_settings")
        .update({
          platform_commission_rate: rate,
          minimum_payout_amount: minAmt,
          payout_holding_days: days,
          network_holding_days: networkPayload,
          updated_at: new Date().toISOString(),
        })
        .eq("id", true);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Settings saved");
      qc.invalidateQueries({ queryKey: ["platform-settings"] });
      qc.invalidateQueries({ queryKey: ["admin-overview-metrics"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const saveBranding = useMutation({
    mutationFn: async () => {
      if (!platformName.trim()) throw new Error("Platform name is required");
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(supportEmail)) throw new Error("Invalid support email");
      const { error } = await supabase
        .from("platform_settings")
        .update({
          platform_name: platformName.trim(),
          support_email: supportEmail.trim(),
          platform_tagline: tagline.trim(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", true);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Branding saved");
      qc.invalidateQueries({ queryKey: ["platform-settings"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const setMaintenance = useMutation({
    mutationFn: async (value: boolean) => {
      const { error } = await supabase
        .from("platform_settings")
        .update({ maintenance_mode: value, updated_at: new Date().toISOString() })
        .eq("id", true);
      if (error) throw error;
      return value;
    },
    onSuccess: (value) => {
      toast.success(value ? "Maintenance mode enabled" : "Maintenance mode disabled");
      qc.invalidateQueries({ queryKey: ["platform-settings"] });
      qc.invalidateQueries({ queryKey: ["maintenance-mode"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-serif text-2xl">Settings</h2>
        <p className="mt-1 text-sm text-muted-foreground">Platform commission, payout threshold and holding period.</p>
      </div>

      <section className="border border-border bg-card">
        <div className="border-b border-border px-5 py-3">
          <h2 className="font-serif text-lg">Commission settings</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Controls how each new commission is split between the platform and the creator. Changes apply to commissions created after saving.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 p-4 sm:gap-5 sm:p-5 md:grid-cols-3">
          <Field
            label="Platform commission rate"
            hint="Percent of gross taken by the platform"
            suffix="%"
            value={ratePct}
            onChange={setRatePct}
            disabled={isLoading}
            inputMode="decimal"
          />
          <Field
            label="Minimum payout"
            hint="Creators must reach this balance to receive a payout"
            suffix="GBP"
            value={minPayout}
            onChange={setMinPayout}
            disabled={isLoading}
            inputMode="decimal"
          />
          <Field
            label="Holding period"
            hint="Days a commission stays pending before becoming payable"
            suffix="days"
            value={holdingDays}
            onChange={setHoldingDays}
            disabled={isLoading}
            inputMode="numeric"
          />
        </div>
        <div className="flex flex-col-reverse gap-3 border-t border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <p className="text-xs text-muted-foreground">
            {data?.updated_at ? `Last updated ${new Date(data.updated_at).toLocaleString()}` : "—"}
          </p>
          <Button onClick={() => save.mutate()} disabled={save.isPending || isLoading}>
            {save.isPending ? "Saving…" : "Save settings"}
          </Button>
        </div>
      </section>

      <section className="border border-border bg-card">
        <div className="border-b border-border px-5 py-3">
          <h2 className="font-serif text-lg">Network-specific clearance windows</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Override the global holding period for a specific affiliate network. Leave blank to use the default above. Saved with the commission settings.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 p-4 sm:gap-5 sm:p-5 md:grid-cols-4">
          {NETWORKS.map((n) => (
            <Field
              key={n}
              label={n}
              hint="Days commissions hold before payout"
              suffix="days"
              value={networkHolds[n] ?? ""}
              onChange={(v) => setNetworkHolds((prev) => ({ ...prev, [n]: v }))}
              disabled={isLoading}
              inputMode="numeric"
            />
          ))}
        </div>
      </section>

      <PlatformBranding
        platformName={platformName}
        setPlatformName={setPlatformName}
        supportEmail={supportEmail}
        setSupportEmail={setSupportEmail}
        tagline={tagline}
        setTagline={setTagline}
        onSave={() => saveBranding.mutate()}
        saving={saveBranding.isPending}
        disabled={isLoading}
      />

      <AdminAccount />

      <section className="border border-border bg-card p-4 sm:p-5">
        <h2 className="font-serif text-lg">Two-factor authentication</h2>
        <div className="mt-4">
          <TwoFactorSettings />
        </div>
        <div className="mt-4">
          <AuthMethodBadge />
        </div>
      </section>

      <MaintenanceMode
        enabled={!!data?.maintenance_mode}
        onToggle={(v) => setMaintenance.mutate(v)}
        disabled={isLoading || setMaintenance.isPending}
      />
    </div>
  );
}

function PlatformBranding({
  platformName, setPlatformName,
  supportEmail, setSupportEmail,
  tagline, setTagline,
  onSave, saving, disabled,
}: {
  platformName: string; setPlatformName: (v: string) => void;
  supportEmail: string; setSupportEmail: (v: string) => void;
  tagline: string; setTagline: (v: string) => void;
  onSave: () => void; saving: boolean; disabled: boolean;
}) {
  return (
    <section className="border border-border bg-card">
      <div className="border-b border-border px-5 py-3">
        <h2 className="font-serif text-lg">Platform branding</h2>
        <p className="mt-1 text-xs text-muted-foreground">Public-facing platform identity and contact email.</p>
      </div>
      <div className="grid grid-cols-1 gap-4 p-4 sm:gap-5 sm:p-5 md:grid-cols-3">
        <Field label="Platform name" value={platformName} onChange={setPlatformName} disabled={disabled} />
        <Field label="Support email" value={supportEmail} onChange={setSupportEmail} disabled={disabled} />
        <Field label="Platform tagline" value={tagline} onChange={setTagline} disabled={disabled} />
      </div>
      <div className="flex items-center justify-end gap-3 border-t border-border px-4 py-3 sm:px-5">
        <Button onClick={onSave} disabled={saving || disabled}>
          {saving ? "Saving…" : "Save branding"}
        </Button>
      </div>
    </section>
  );
}

function AdminAccount() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pw1.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw1 !== pw2) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password: pw1 });
    setLoading(false);
    if (error) { toast.error(error.message); return; }
    setPw1(""); setPw2(""); setOpen(false);
    toast.success("Password updated");
  };

  return (
    <section className="border border-border bg-card">
      <div className="border-b border-border px-5 py-3">
        <h2 className="font-serif text-lg">Admin account</h2>
        <p className="mt-1 text-xs text-muted-foreground">Signed in as {user?.email ?? "—"}</p>
      </div>
      <div className="p-4 sm:p-5">
        {!open ? (
          <Button variant="outline" onClick={() => setOpen(true)}>Change password</Button>
        ) : (
          <form onSubmit={submit} className="max-w-md space-y-3">
            <div>
              <label htmlFor="admin-new-password" className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground">New password</label>
              <Input id="admin-new-password" type="password" minLength={8} required value={pw1} onChange={(e) => setPw1(e.target.value)} />
            </div>
            <div>
              <label htmlFor="admin-confirm-password" className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground">Confirm password</label>
              <Input id="admin-confirm-password" type="password" minLength={8} required value={pw2} onChange={(e) => setPw2(e.target.value)} />
            </div>
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={loading}>{loading ? "Updating…" : "Update password"}</Button>
              <Button type="button" variant="outline" onClick={() => { setOpen(false); setPw1(""); setPw2(""); }}>Cancel</Button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

function MaintenanceMode({ enabled, onToggle, disabled }: { enabled: boolean; onToggle: (v: boolean) => void; disabled: boolean }) {
  return (
    <section className="border border-border bg-card">
      <div className="border-b border-border px-5 py-3">
        <h2 className="font-serif text-lg">Maintenance mode</h2>
      </div>
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start sm:justify-between sm:p-5">
        <div>
          <p className="text-sm">Enable maintenance mode</p>
          <p className="mt-1 text-xs text-muted-foreground">
            When enabled, public pages show a maintenance message. Admin and creator dashboards remain accessible.
          </p>
        </div>
        <Switch checked={enabled} onCheckedChange={onToggle} disabled={disabled} />
      </div>
    </section>
  );
}

function Field({
  label,
  hint,
  suffix,
  value,
  onChange,
  disabled,
  inputMode,
}: {
  label: string;
  hint?: string;
  suffix?: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  inputMode?: "decimal" | "numeric";
}) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <div className="mt-2 flex items-center gap-2">
        <Input value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} inputMode={inputMode} />
        {suffix && <span className="shrink-0 text-xs text-muted-foreground">{suffix}</span>}
      </div>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </label>
  );
}